import { DateFormatterCollection, dateFormatterFactory } from '../formatting/dateFormatter';
import { Messages } from '../localization';
import {
  Features,
  EffectiveDefinition,
  ValueAccessor,
  DateGranularity,
  FiscalYearStartMonth,
  dateTruncFromGranularity,
} from '../models';
import {
  ColumnReference,
  columnSchemaForRef,
  DateTimeColumnSchema,
  NumericColumnSchema,
} from '../models/EffectiveDefinition';
import { isBeingWarehoused } from './warehousing';

export const TIMESTAMP_COLUMN_NAME = 'timestamp';
export const MEASURE_COLUMN_NAME = 'measure';

export const getTimestampFeature = (features: Features): ColumnReference => {
  return features.timestamp;
};

export const getMeasureFeature = (features: Features): ColumnReference => {
  return features.measure;
};

export const buildTimestampAccessor = (effectiveDefinition: EffectiveDefinition): ValueAccessor<Date | undefined> => {
  const timestamp = getTimestampFeature(effectiveDefinition.features);
  return (dataRow) => dataRow[timestamp?.column] as Date;
};

export const buildMeasureAccessor = (effectiveDefinition: EffectiveDefinition): ValueAccessor<number> => {
  const measure = getMeasureFeature(effectiveDefinition.features);
  return (dataRow) => dataRow[measure.column] as number;
};

export const getTimestampGranularity = (effectiveDefinition: EffectiveDefinition): DateGranularity => {
  const timestamp = getTimestampFeature(effectiveDefinition.features);
  const column = columnSchemaForRef(effectiveDefinition, timestamp);

  // Phoenix needs to be backwards compatible with oldest supported version (2020.4 as of Feb '22),
  // which doesn't return a `type` for the ColumnSchema, so we are casting here rather than using
  // isDateTimeColumnSchema. Once the oldest supported version does use `type` (2021.1), this should
  // be modified to use isDateTimeColumnSchema instead.
  return (column as DateTimeColumnSchema)?.dateGranularity ?? DateGranularity.UNKNOWN;
};

export const getMeasureFormatString = (definition: EffectiveDefinition): string | undefined => {
  const measure = getMeasureFeature(definition.features);

  const column = columnSchemaForRef(definition, measure);

  // Phoenix needs to be backwards compatible with oldest supported version (2020.4 as of Feb '22),
  // which doesn't return a `type` for the ColumnSchema, so we are casting here rather than using
  // isDateTimeColumnSchema. Once the oldest supported version does use `type` (2021.1), this should
  // be modified to use isDateTimeColumnSchema instead.
  return (column as NumericColumnSchema)?.format;
};

export const getFiscalYearStartMonth = (definition?: EffectiveDefinition): FiscalYearStartMonth => {
  const timestamp = definition && getTimestampFeature(definition.features);
  const column = definition && timestamp && columnSchemaForRef(definition, timestamp);

  // Phoenix needs to be backwards compatible with oldest supported version (2020.4 as of Feb '22),
  // which doesn't return a `type` for the ColumnSchema, so we are casting here rather than using
  // isDateTimeColumnSchema. Once the oldest supported version does use `type` (2021.1), this should
  // be modified to use isDateTimeColumnSchema instead.
  return (column as DateTimeColumnSchema)?.fiscalYearStartMonth ?? FiscalYearStartMonth.JANUARY;
};

export const getDateFormatter = (definition?: EffectiveDefinition): DateFormatterCollection => {
  const granularity = definition ? getTimestampGranularity(definition) : DateGranularity.UNKNOWN;
  const dateTrunc = dateTruncFromGranularity(granularity);
  const fiscalYearStartMonth = getFiscalYearStartMonth(definition);
  return dateFormatterFactory(dateTrunc, fiscalYearStartMonth);
};

/**
 * Primarily used to determine whether the metric should be displayed as a single number, what
 * information to include in the configuration section, and whether a new or edited config is valid.
 * Specifically, a metric where isTimelessWithWarehousingDisabled returns false cannot be saved if it
 * does not have a valid date window.
 *
 * @param definition the metric EffectiveDefinition
 * @returns true for timeless metrics that are not being warehoused, false otherwise
 */
export const isTimelessWithWarehousingDisabled = (definition: EffectiveDefinition): boolean => {
  // casting instead of using `isDateTimeColumnSchema` to support Phoenix accessing a 2020.4 server
  const timestampColumn = definition?.schema?.timestamp as DateTimeColumnSchema | undefined;

  if (timestampColumn === undefined) {
    return true;
  }

  // With the release of snapshotting/warehousing, we no longer want to use, and are deprecating, isSnapshotTimestamp.
  // We also can't simply remove the old return since metrics-engine is used by Phoenix and may access older servers.
  if (timestampColumn.isTimeless !== undefined) {
    return timestampColumn.isTimeless && !isBeingWarehoused(definition?.features);
  }

  return timestampColumn.isSnapshotTimestamp === true;
};

/**
 * Primarily used to determine whether the strings associated with the metric should read 'snapshotted' (if true)
 * or 'refreshed' (if false).
 *
 * @param definition the metric EffectiveDefinition
 * @returns true for timeless metrics that are being warehoused, false otherwise
 */
export const isTimelessWithWarehousingEnabled = (definition: EffectiveDefinition): boolean => {
  // casting instead of using `isDateTimeColumnSchema` to support Phoenix accessing a 2020.4 server
  const timestampColumn = definition?.schema?.timestamp as DateTimeColumnSchema | undefined;

  if (timestampColumn === undefined) {
    return false;
  }

  if (timestampColumn.isTimeless !== undefined) {
    return timestampColumn.isTimeless && isBeingWarehoused(definition?.features);
  }

  return false;
};

export const timelessMetricTimeDimension = (isWarehoused: boolean): string => {
  return isWarehoused ? Messages.snapshotTimeDimension() : Messages.noTimeDimensionPlaceholder();
};
